// src/components/TrackingModal.jsx
import React, { useEffect } from 'react';
import {
  FiX, FiMapPin, FiClock, FiCheckCircle,
  FiTruck, FiPackage, FiAlertCircle,
  FiExternalLink, FiNavigation, FiRefreshCw, FiBox,
  FiDollarSign, FiHash
} from 'react-icons/fi';

/* ─────────────────────────────────────────
   STATUS HELPERS
───────────────────────────────────────── */
const getStatusMeta = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('delivered') && !s.includes('out'))
    return { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-800', dot: 'bg-green-500', ringColor: 'ring-green-200', icon: <FiCheckCircle className="text-green-600" size={20} /> };
  if (s.includes('out for') || s.includes('out-for'))
    return { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500', ringColor: 'ring-orange-200', icon: <FiNavigation className="text-orange-600" size={20} /> };
  if (s.includes('transit') || s.includes('in transit'))
    return { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500', ringColor: 'ring-blue-200', icon: <FiTruck className="text-blue-600" size={20} /> };
  if (s.includes('pickup') || s.includes('picked'))
    return { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500', ringColor: 'ring-purple-200', icon: <FiPackage className="text-purple-600" size={20} /> };
  if (s.includes('ship'))
    return { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-800', dot: 'bg-indigo-500', ringColor: 'ring-indigo-200', icon: <FiTruck className="text-indigo-600" size={20} /> };
  if (s.includes('cancel') || s.includes('rto') || s.includes('return'))
    return { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', dot: 'bg-red-500', ringColor: 'ring-red-200', icon: <FiAlertCircle className="text-red-600" size={20} /> };
  if (s.includes('processing') || s.includes('booked') || s.includes('placed'))
    return { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500', ringColor: 'ring-yellow-200', icon: <FiBox className="text-yellow-600" size={20} /> };
  return { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400', ringColor: 'ring-gray-200', icon: <FiClock className="text-gray-500" size={20} /> };
};

const getDotStyle = (status = '', isFirst = false) => {
  if (!isFirst) return 'bg-gray-300 ring-gray-100';
  const meta = getStatusMeta(status);
  return `${meta.dot} ${meta.ringColor}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return dateStr; }
};

/* ─────────────────────────────────────────
   PROGRESS STEPS
───────────────────────────────────────── */
const PROGRESS_STEPS = [
  { key: 'placed',    label: 'Placed',     icon: <FiBox size={14} />          },
  { key: 'picked',    label: 'Picked',     icon: <FiPackage size={14} />      },
  { key: 'transit',   label: 'In Transit', icon: <FiTruck size={14} />        },
  { key: 'delivery',  label: 'Out for Del',icon: <FiNavigation size={14} />   },
  { key: 'delivered', label: 'Delivered',  icon: <FiCheckCircle size={14} />  },
];

const getProgressIndex = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('delivered') && !s.includes('out')) return 4;
  if (s.includes('out for'))                          return 3;
  if (s.includes('transit'))                          return 2;
  if (s.includes('pick'))                             return 1;
  return 0;
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
   Props:
     trackingData: {
       awb, status, courier, expectedDelivery,
       origin, destination,
       shipmentDetails: { weight, mode, paymentMode, codAmount, invoiceNo, products[] },
       history: [{ status, date, location, remarks }]
     }
     onClose: () => void
───────────────────────────────────────── */
const TrackingModal = ({ trackingData, onClose }) => {
  const meta        = getStatusMeta(trackingData?.status);
  const progressIdx = getProgressIndex(trackingData?.status);
  const history     = trackingData?.history || [];
  const lastUpdated = history[0]?.date;
  const sd          = trackingData?.shipmentDetails || {};

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!trackingData) return null;

  const { awb = '', status = 'Processing', courier = '', expectedDelivery, origin = '', destination = '' } = trackingData;

  return (
    <>
      {/* ── Backdrop ── */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />

      {/* ── Modal ── */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div
          className="pointer-events-auto bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: '92vh', animation: 'trackModalIn 0.3s cubic-bezier(0.32,0.72,0,1)' }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ══ HEADER ══ */}
          <div className="shrink-0 px-5 pt-4 pb-4 border-b border-gray-100">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3 sm:hidden" />
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                  <FiTruck className="text-red-500" size={18} />
                  Shipment Tracking
                </h3>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="text-xs text-gray-400 font-medium">AWB</span>
                  <span className="text-xs font-mono font-bold text-gray-800 bg-gray-100 px-2.5 py-0.5 rounded-lg tracking-wider select-all">
                    {awb}
                  </span>
                  {courier && (
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">
                      {courier}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-600 transition-colors shrink-0 ml-3"
              >
                <FiX size={16} />
              </button>
            </div>
          </div>

          {/* ══ SCROLLABLE BODY ══ */}
          <div className="flex-1 overflow-y-auto overscroll-contain">

            {/* ── Current Status Banner ── */}
            <div className={`mx-4 mt-4 p-4 rounded-2xl border ${meta.bg} ${meta.border}`}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${meta.border} bg-white shrink-0 shadow-sm`}>
                  {meta.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-extrabold px-3 py-1 rounded-full inline-block capitalize ${meta.badge}`}>
                    {status}
                  </span>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {expectedDelivery && (
                      <p className="text-xs text-gray-600 flex items-center gap-1 font-medium">
                        <FiClock size={10} className="text-gray-400" />
                        Expected: {new Date(expectedDelivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                    {lastUpdated && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <FiRefreshCw size={10} />
                        Updated: {formatDate(lastUpdated)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Progress Steps Bar ── */}
            <div className="mx-4 mt-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="relative">
                <div className="absolute top-[18px] left-0 right-0 h-1 bg-gray-200 rounded-full" />
                <div
                  className="absolute top-[18px] left-0 h-1 bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all duration-700"
                  style={{ width: `${(progressIdx / (PROGRESS_STEPS.length - 1)) * 100}%` }}
                />
                <div className="relative flex justify-between">
                  {PROGRESS_STEPS.map((step, idx) => {
                    const done    = idx <= progressIdx;
                    const current = idx === progressIdx;
                    return (
                      <div key={step.key} className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center border-4 border-white shadow-sm z-10 transition-all ${
                          done ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`}>
                          {step.icon}
                        </div>
                        <span className={`mt-1.5 text-center leading-tight ${
                          current ? 'text-red-600 font-extrabold' :
                          done    ? 'text-gray-700 font-bold'     :
                          'text-gray-400 font-medium'
                        }`} style={{ fontSize: '10px' }}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Shipment Info Cards (Route + Details) ── */}
            <div className="mx-4 mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'From',     value: origin      || '—', icon: <FiNavigation size={11} /> },
                { label: 'To',       value: destination || '—', icon: <FiMapPin size={11} />     },
                { label: 'Mode',     value: sd.expressType ? sd.expressType.charAt(0).toUpperCase() + sd.expressType.slice(1) : (sd.mode || 'Surface'), icon: <FiTruck size={11} /> },
                { label: 'Payment',  value: sd.paymentMode || '—', icon: <FiDollarSign size={11} /> },
              ].map(({ label, value, icon }) => (
                <div key={label} className="bg-white border border-gray-100 rounded-xl px-3 py-2.5 shadow-sm">
                  <div className="flex items-center gap-1 text-gray-400 mb-1">
                    {icon}
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                  <p className="text-xs font-bold text-gray-800 truncate capitalize">{value}</p>
                </div>
              ))}
            </div>

            {/* ── Shipment Details Card ── */}
            {(sd.invoiceNo || sd.codAmount || sd.orderAmount || (sd.products && sd.products.length > 0)) && (
              <div className="mx-4 mt-3 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <FiHash size={11} /> Shipment Details
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                  {sd.invoiceNo && (
                    <div>
                      <p className="text-gray-400 font-medium">Invoice No.</p>
                      <p className="font-bold text-gray-800 font-mono">{sd.invoiceNo}</p>
                    </div>
                  )}
                  {(sd.codAmount > 0 || sd.orderAmount > 0) && (
                    <div>
                      <p className="text-gray-400 font-medium">{sd.codAmount > 0 ? 'COD Amount' : 'Order Amount'}</p>
                      <p className="font-bold text-gray-800">₹{(sd.codAmount || sd.orderAmount || 0).toLocaleString('en-IN')}</p>
                    </div>
                  )}
                  {sd.products && sd.products.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-gray-400 font-medium mb-1">Products</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sd.products.map((p, i) => (
                          <span key={i} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg font-medium text-xs">
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Consignee Info (if available) ── */}
            {destination && (
              <div className="mx-4 mt-3 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                  <FiMapPin size={11} /> Delivery Address
                </h4>
                <div className="text-xs text-gray-700 space-y-0.5">
                  {sd.consigneeName && <p className="font-bold text-gray-900">{sd.consigneeName}</p>}
                  {sd.addressLine1 && <p>{sd.addressLine1}</p>}
                  <p className="text-gray-500">{destination}{sd.pincode ? ` - ${sd.pincode}` : ''}</p>
                  {sd.state && <p className="text-gray-400">{sd.state}</p>}
                </div>
              </div>
            )}

            {/* ── Tracking Timeline ── */}
            <div className="px-4 pt-4 pb-2">
              <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FiClock size={14} className="text-gray-400" />
                Tracking Timeline
                {history.length > 0 && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                    {history.length} event{history.length !== 1 ? 's' : ''}
                  </span>
                )}
              </h4>

              {history.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <FiClock size={24} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-semibold text-gray-600">No events yet</p>
                  <p className="text-xs mt-1 text-gray-400 max-w-xs mx-auto leading-relaxed">
                    Shipment is being processed. Check back in a few hours.
                  </p>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-[6px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-gray-300 via-gray-200 to-transparent rounded-full" />
                  <div className="space-y-1.5">
                    {history.map((event, idx) => {
                      const isFirst = idx === 0;
                      return (
                        <div
                          key={idx}
                          className="relative flex gap-3.5"
                          style={{ animation: `trackFadeIn 0.2s ease ${idx * 0.04}s both` }}
                        >
                          <div className={`relative z-10 w-3.5 h-3.5 rounded-full shrink-0 mt-3 ring-4 ${getDotStyle(event.status, isFirst)}`} />
                          <div className={`flex-1 mb-1 px-3.5 py-3 rounded-xl border ${
                            isFirst ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50/80 border-gray-100'
                          }`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm leading-snug font-bold capitalize ${isFirst ? 'text-gray-900' : 'text-gray-600'}`}>
                                  {event.status || 'Status Update'}
                                </p>
                                {event.location && (
                                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1 truncate">
                                    <FiMapPin size={10} className="shrink-0" />
                                    {event.location}
                                  </p>
                                )}
                                {event.remarks && event.remarks.toLowerCase() !== event.status?.toLowerCase() && (
                                  <p className="text-xs text-gray-400 mt-1 leading-relaxed capitalize">{event.remarks}</p>
                                )}
                              </div>
                              {event.date && (
                                <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 mt-0.5 text-right">
                                  {formatDate(event.date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="h-3" />
          </div>

          {/* ══ FOOTER ══ */}
          <div className="shrink-0 bg-white border-t border-gray-100 px-4 py-4 flex gap-3">
            <a
              href="https://parcelx.in/tracking.php"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 border-2 border-gray-200 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-center gap-2"
            >
              <FiExternalLink size={14} />
              Track on ParcelX
            </a>
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 active:scale-95 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes trackModalIn {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes trackFadeIn {
          from { transform: translateY(5px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @media (min-width: 640px) {
          @keyframes trackModalIn {
            from { transform: scale(0.95) translateY(10px); opacity: 0; }
            to   { transform: scale(1)    translateY(0);    opacity: 1; }
          }
        }
      `}</style>
    </>
  );
};

export default TrackingModal;